import React, { useState, useRef, useEffect } from 'react';
import { 
  Building2, FileCheck, Search, BarChart3, Package, Unlock, 
  Truck, LineChart, Zap, Phone, Check, ChevronDown, Play, Video, ArrowRight, Star, Users
} from 'lucide-react';

const calls = [
  { num: 1, icon: Building2, title: 'Открытие компании', result: 'Компания готова', color: 'amber',
    description: 'Какую форму выбрать, где регистрировать',
    topics: ['Выбор страны', 'Форма собственности', 'Стоимость регистрации', 'Банковский счёт', 'Документы'] },
  { num: 2, icon: FileCheck, title: 'Регистрация аккаунта', result: 'Аккаунт активен', color: 'emerald',
    description: 'Как пройти верификацию с первого раза',
    topics: ['Подготовка документов', 'Заполнение заявки', 'Видео-верификация', 'Типичные ошибки', 'Что делать при отказе'] },
  { num: 3, icon: Search, title: 'Поиск товаров', result: 'Список товаров', color: 'blue',
    description: 'Где искать, на что смотреть',
    topics: ['Критерии отбора', 'Инструменты поиска', 'Анализ конкурентов', 'Оценка спроса', 'Риски'] },
  { num: 4, icon: BarChart3, title: 'Расчёт прибыли', result: 'Понимаете цифры', color: 'violet',
    description: 'Все комиссии, реальная маржа',
    topics: ['Комиссии Amazon', 'Расчёт ROI', 'Точка безубытка', 'Скрытые расходы', 'Калькулятор'] },
  { num: 5, icon: Package, title: 'Подготовка товара', result: 'Товар готов', color: 'rose',
    description: 'Маркировка, упаковка, требования',
    topics: ['FNSKU', 'Штрих-коды', 'Упаковка', 'Требования Amazon', 'Преп-центры'] },
  { num: 6, icon: Unlock, title: 'Открытие брендов', result: 'Доступ к брендам', color: 'amber',
    description: 'Доступ к закрытым категориям',
    topics: ['Типы ограничений', 'Письма поставщикам', 'Апелляции', 'Ungating стратегии'] },
  { num: 7, icon: Truck, title: 'Отправка в Amazon', result: 'Товар на складе', color: 'emerald',
    description: 'Логистика от поставщика до склада',
    topics: ['Создание шипмента', 'Выбор склада', 'Транспортные компании', 'Отслеживание', 'Проблемы'] },
  { num: 8, icon: LineChart, title: 'Продажи и Buy Box', result: 'Первые заказы', color: 'blue',
    description: 'Как выигрывать Buy Box',
    topics: ['Факторы Buy Box', 'Ценообразование', 'Репрайсер', 'Конкуренты', 'Оптимизация'] },
  { num: 9, icon: Zap, title: 'Масштабирование', result: 'Бизнес растёт', color: 'violet',
    description: 'Больше товаров, больше прибыли',
    topics: ['Расширение ассортимента', 'Новые поставщики', 'Автоматизация', 'Команда', 'Новые рынки'] },
];

const colorMap = {
  amber: { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/40', shadow: 'shadow-amber-500/30', glow: 'bg-amber-500/20' },
  emerald: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/40', shadow: 'shadow-emerald-500/30', glow: 'bg-emerald-500/20' },
  blue: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/40', shadow: 'shadow-blue-500/30', glow: 'bg-blue-500/20' },
  violet: { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/40', shadow: 'shadow-violet-500/30', glow: 'bg-violet-500/20' },
  rose: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/40', shadow: 'shadow-rose-500/30', glow: 'bg-rose-500/20' },
};

const CallItem = ({ call, isOpen, onToggle, index }) => {
  const Icon = call.icon;
  const colors = colorMap[call.color];
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) setContentHeight(contentRef.current.scrollHeight);
  }, [isOpen]);

  return (
    <div data-testid={`call-item-${call.num}`} className="group" style={{ animationDelay: `${index * 30}ms` }}>
      <div className={`relative rounded-2xl border-2 overflow-hidden transition-all duration-500 ${
        isOpen 
          ? `${colors.bg} ${colors.border} shadow-2xl ${colors.shadow}` 
          : 'bg-zinc-900/40 border-zinc-800/60 hover:border-zinc-700 hover:bg-zinc-900/60'
      }`}>
        
        {/* Glow */}
        {isOpen && <div className={`absolute -inset-2 ${colors.glow} rounded-3xl blur-2xl -z-10 animate-pulse`} />}
        
        {/* Top accent */}
        <div className={`h-1 bg-gradient-to-r ${colors.gradient} transition-transform duration-500 origin-left ${isOpen ? 'scale-x-100' : 'scale-x-0'}`} />
        
        {/* Header */}
        <button 
          onClick={onToggle}
          data-testid={`call-toggle-${call.num}`}
          className="w-full p-4 sm:p-5 cursor-pointer select-none text-left"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        >
          <div className="flex items-start sm:items-center gap-3 sm:gap-4">
            {/* Number */}
            <div className={`relative w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center font-black text-base sm:text-xl bg-gradient-to-br ${colors.gradient} text-white flex-shrink-0 transition-all duration-500 ${isOpen ? 'scale-110 rotate-3' : 'group-hover:scale-105'}`}>
              {call.num}
              {isOpen && <div className={`absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br ${colors.gradient} animate-ping opacity-30`} />}
            </div>
            
            {/* Text + Icon container */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
                {/* Icon - hidden on mobile */}
                <div className={`hidden sm:flex w-12 h-12 rounded-xl items-center justify-center ${colors.bg} flex-shrink-0 transition-all duration-300 ${isOpen ? `ring-2 ring-offset-2 ring-offset-zinc-900 ${colors.border}` : ''}`}>
                  <Icon className={`w-6 h-6 ${colors.text} transition-transform duration-300 ${isOpen ? 'scale-110' : ''}`} />
                </div>
                
                {/* Title and description */}
                <div className="flex-1 min-w-0">
                  <h4 className={`font-black text-base sm:text-xl transition-colors duration-300 ${isOpen ? 'text-white' : 'text-zinc-200'}`}>
                    {call.title}
                  </h4>
                  <p className={`text-xs sm:text-sm transition-colors duration-300 mt-0.5 ${isOpen ? colors.text : 'text-zinc-500'}`}>
                    {call.description}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Arrow */}
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center transition-all duration-500 flex-shrink-0 ${isOpen ? `bg-gradient-to-br ${colors.gradient}` : 'bg-zinc-800'}`}>
              <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-500 ${isOpen ? 'text-white rotate-180' : 'text-zinc-500'}`} />
            </div>
          </div>
        </button>
        
        {/* Content */}
        <div 
          className="overflow-hidden transition-all duration-500 ease-out"
          style={{ maxHeight: isOpen ? `${contentHeight}px` : '0px', opacity: isOpen ? 1 : 0 }}
        >
          <div ref={contentRef} className="px-4 sm:px-5 pb-4 sm:pb-5">
            <div className="pt-3 sm:pt-4 border-t border-zinc-800/50">
              {/* Result badge */}
              <div className={`inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl ${colors.bg} border ${colors.border} mb-3 sm:mb-4`}>
                <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-md sm:rounded-lg bg-gradient-to-br ${colors.gradient} flex items-center justify-center`}>
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className={`font-black text-sm sm:text-lg ${colors.text}`}>Результат: {call.result}</span>
              </div>
              
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Play className={`w-3 h-3 sm:w-4 sm:h-4 ${colors.text}`} />
                <span className={`text-xs sm:text-sm font-bold uppercase tracking-wider ${colors.text}`}>Что разберём</span>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {call.topics.map((topic, i) => (
                  <div 
                    key={i} 
                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl bg-zinc-800/80 text-zinc-100 text-xs sm:text-sm font-medium border border-zinc-700/50 hover:border-zinc-600 transition-all duration-300 hover:-translate-y-0.5"
                  >
                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gradient-to-r ${colors.gradient} flex-shrink-0`} />
                    <span className="whitespace-nowrap">{topic}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgramSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openCallNum, setOpenCallNum] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]" id="calls">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 right-0 w-[600px] h-[600px] bg-violet-500/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 left-0 w-[500px] h-[500px] bg-amber-500/[0.03] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30 mb-6 backdrop-blur-xl">
            <Phone className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 font-bold text-sm tracking-wide">ЖИВЫЕ СОЗВОНЫ</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-white">Темы </span>
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-violet-400 bg-clip-text text-transparent">созвонов</span>
          </h2>
          <p className="text-xl text-zinc-400">9 созвонов • Вживую или в записи</p>
        </div>

        {/* Main Toggle - MEGA */}
        <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative group">
            {/* Glow */}
            <div className={`absolute -inset-2 bg-gradient-to-r from-violet-500/30 via-purple-500/30 to-violet-500/30 rounded-3xl blur-xl transition-opacity duration-500 ${isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
            
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              data-testid="calls-main-toggle"
              className={`relative w-full flex items-center justify-between gap-6 p-6 sm:p-8 rounded-3xl border-2 cursor-pointer select-none transition-all duration-500 ${
                isExpanded 
                  ? 'bg-gradient-to-br from-violet-500/15 via-purple-500/10 to-violet-500/15 border-violet-500/50 shadow-2xl shadow-violet-500/20' 
                  : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/80'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              <div className="flex items-center gap-5">
                <div className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                  isExpanded ? 'bg-gradient-to-br from-violet-500 to-purple-600 rotate-3' : 'bg-zinc-800 group-hover:bg-zinc-700'
                }`}>
                  <Phone className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  {isExpanded && <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 animate-pulse opacity-50" />}
                </div>
                
                <div className="text-left">
                  <h3 className="text-2xl sm:text-3xl font-black text-white mb-1">9 созвонов</h3>
                  <p className={`text-lg font-semibold transition-colors duration-300 ${isExpanded ? 'text-violet-400' : 'text-zinc-500'}`}>
                    Живые разборы или записи
                  </p>
                </div>
              </div>
              
              <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-black text-lg transition-all duration-500 ${
                isExpanded 
                  ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg shadow-violet-500/30' 
                  : 'bg-zinc-800 text-zinc-300 group-hover:bg-zinc-700'
              }`}>
                <span className="hidden sm:inline">{isExpanded ? 'Скрыть' : 'Раскрыть темы'}</span>
                <ChevronDown className={`w-6 h-6 transition-transform duration-500 ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Calls List */}
        <div className={`overflow-hidden transition-all duration-700 ${isExpanded ? 'max-h-[8000px] opacity-100 mt-6' : 'max-h-0 opacity-0 mt-0'}`}>
          <div className="space-y-3">
            {calls.map((call, index) => (
              <CallItem 
                key={call.num}
                call={call}
                index={index}
                isOpen={openCallNum === call.num}
                onToggle={() => setOpenCallNum(openCallNum === call.num ? null : call.num)}
              />
            ))}
            
            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 p-6 rounded-2xl bg-gradient-to-r from-violet-500/10 via-purple-500/10 to-violet-500/10 border border-violet-500/30 mt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <span className="text-white font-bold text-lg">Живые созвоны</span>
              </div>
              <div className="text-zinc-500 text-2xl hidden sm:block">или</div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                  <Play className="w-6 h-6 text-white" />
                </div>
                <span className="text-zinc-300 font-bold text-lg">Записи навсегда</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramSection;
